import { useState, useEffect } from 'react';
import { Check, Plus, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import { questionsApi } from '../../../services/api';

interface SwotChecklistProps {
  entityType: string;
  companyId: string;
  selectedItems: string[];
  onSelectionChange: (items: string[]) => void;
  color: string;
}

interface QuestionItem {
  id: string;
  content: string;
  isPreset: boolean;
  responses: Array<{
    id: string;
    response: any;
    score: number;
    answeredAt: string;
  }>;
}

type ImpactLevel = 'low' | 'medium' | 'high';

export default function SwotChecklist({
  entityType,
  companyId,
  selectedItems,
  onSelectionChange,
  color,
}: SwotChecklistProps) {
  const [questions, setQuestions] = useState<QuestionItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newItemText, setNewItemText] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [isSavingCustom, setIsSavingCustom] = useState(false);
  const [impactLevels, setImpactLevels] = useState<Record<string, ImpactLevel>>({});

  useEffect(() => {
    const loadQuestions = async () => {
      try {
        setIsLoading(true);
        const response = await questionsApi.getEntityQuestions(entityType, companyId);
        if (response.success && response.data) {
          setQuestions(response.data);

          // Extract selected items and impact levels from question responses
          const selectedFromResponses: string[] = [];
          const impactFromResponses: Record<string, ImpactLevel> = {};

          response.data.forEach((q) => {
            if (q.responses.length > 0 && q.responses[0].response.booleanValue === true) {
              selectedFromResponses.push(q.content);
              // Load impact level if it exists, default to 'medium'
              const impactLevel = q.responses[0].response.impactLevel || 'medium';
              impactFromResponses[q.content] = impactLevel as ImpactLevel;
            }
          });

          // Only update selection if it's different (to avoid infinite loops)
          if (
            JSON.stringify(selectedFromResponses.sort()) !== JSON.stringify(selectedItems.sort())
          ) {
            onSelectionChange(selectedFromResponses);
          }

          setImpactLevels(impactFromResponses);
        }
      } catch (error) {
        console.error('Failed to load questions:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadQuestions();
  }, [entityType, companyId]);

  const handleItemToggle = async (item: string) => {
    const isCurrentlySelected = selectedItems.includes(item);
    const newSelection = isCurrentlySelected
      ? selectedItems.filter((i) => i !== item)
      : [...selectedItems, item];

    // Find the question for this item
    const question = questions.find((q) => q.content === item);

    if (question) {
      // Save the individual question response
      try {
        const responseData = {
          booleanValue: !isCurrentlySelected,
          impactLevel: !isCurrentlySelected ? impactLevels[item] || 'medium' : undefined,
        } as any;

        await questionsApi.saveResponse(companyId, {
          questionId: question.id,
          entityType,
          response: responseData,
        });

        // Update local impact levels
        if (!isCurrentlySelected) {
          setImpactLevels((prev) => ({
            ...prev,
            [item]: prev[item] || 'medium',
          }));
        } else {
          setImpactLevels((prev) => {
            const updated = { ...prev };
            delete updated[item];
            return updated;
          });
        }
      } catch (error) {
        console.error('Failed to save question response:', error);
      }
    }

    onSelectionChange(newSelection);
  };

  const handleImpactChange = async (item: string, impactLevel: ImpactLevel) => {
    const question = questions.find((q) => q.content === item);

    if (question) {
      try {
        await questionsApi.saveResponse(companyId, {
          questionId: question.id,
          entityType,
          response: {
            booleanValue: true,
            impactLevel: impactLevel,
          } as any,
        });

        setImpactLevels((prev) => ({
          ...prev,
          [item]: impactLevel,
        }));
      } catch (error) {
        console.error('Failed to save impact level:', error);
      }
    }
  };

  const handleAddCustomItem = async () => {
    if (newItemText.trim()) {
      try {
        setIsSavingCustom(true);

        // Create custom question in database
        const questionResponse = await questionsApi.createCustomQuestion(companyId, {
          questionType: entityType,
          content: newItemText.trim(),
        });

        if (questionResponse.success && questionResponse.data) {
          // Save initial response (selected = true, default medium impact)
          await questionsApi.saveResponse(companyId, {
            questionId: questionResponse.data.id,
            entityType,
            response: {
              booleanValue: true,
              impactLevel: 'medium',
            } as any,
          });

          // Add to local state
          const newQuestion: QuestionItem = {
            id: questionResponse.data.id,
            content: questionResponse.data.content,
            isPreset: false,
            responses: [
              {
                id: 'temp',
                response: { booleanValue: true, impactLevel: 'medium' },
                score: 0,
                answeredAt: new Date().toISOString(),
              },
            ],
          };

          setQuestions((prev) => [...prev, newQuestion]);
          onSelectionChange([...selectedItems, newItemText.trim()]);
          setImpactLevels((prev) => ({
            ...prev,
            [newItemText.trim()]: 'medium',
          }));
        }

        setNewItemText('');
        setIsAddingNew(false);
      } catch (error) {
        console.error('Failed to create custom question:', error);
      } finally {
        setIsSavingCustom(false);
      }
    }
  };

  const isItemSelected = (item: string) => selectedItems.includes(item);

  const getColorClasses = (selected: boolean) => {
    if (selected) {
      switch (color) {
        case 'text-green-600':
          return 'bg-green-50 border-green-300 text-green-800';
        case 'text-red-600':
          return 'bg-red-50 border-red-300 text-red-800';
        case 'text-blue-600':
          return 'bg-blue-50 border-blue-300 text-blue-800';
        case 'text-orange-600':
          return 'bg-orange-50 border-orange-300 text-orange-800';
        default:
          return 'bg-gray-50 border-gray-300 text-gray-800';
      }
    }
    return 'bg-white border-gray-200 text-gray-700 hover:border-gray-300';
  };

  const getCheckboxColor = () => {
    switch (color) {
      case 'text-green-600':
        return 'text-green-600';
      case 'text-red-600':
        return 'text-red-600';
      case 'text-blue-600':
        return 'text-blue-600';
      case 'text-orange-600':
        return 'text-orange-600';
      default:
        return 'text-gray-600';
    }
  };

  const getImpactColor = (level: ImpactLevel) => {
    switch (level) {
      case 'low':
        return 'bg-gray-100 text-gray-700 border-gray-300';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'high':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 h-full">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <div className="w-3 h-3 rounded-full bg-purple-500 mr-3" />
            SWOT Items
          </h3>
          <div className="flex items-center space-x-2 px-3 py-1 rounded-lg bg-purple-100">
            <span className="text-sm font-medium text-purple-800">
              {selectedItems.length} selected
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-3 overflow-y-auto max-h-[calc(100%-100px)]">
          {/* Loading State */}
          {isLoading && (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-12 bg-gray-200 rounded-lg"></div>
                </div>
              ))}
            </div>
          )}

          {/* Preset Questions */}
          {!isLoading &&
            questions.map((question) => {
              const isSelected = isItemSelected(question.content);
              const currentImpact = impactLevels[question.content] || 'medium';

              return (
                <motion.div
                  key={question.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className={`border rounded-lg transition-all duration-200 ${getColorClasses(isSelected)}`}
                >
                  <div
                    className="p-4 cursor-pointer"
                    onClick={() => handleItemToggle(question.content)}
                  >
                    <div className="flex items-start space-x-3">
                      <div
                        className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          isSelected ? `${getCheckboxColor()} border-current` : 'border-gray-300'
                        }`}
                      >
                        {isSelected && <Check className="w-3 h-3" />}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium leading-relaxed">{question.content}</p>
                        {!question.isPreset && (
                          <p className="text-xs text-gray-500 mt-1">Custom item</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Impact Level Selection - Only show when selected */}
                  {isSelected && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="px-4 pb-4"
                    >
                      <div className="flex items-center space-x-2 pt-2 border-t border-gray-200">
                        <Zap className="h-4 w-4 text-gray-500" />
                        <span className="text-xs font-medium text-gray-600">Impact:</span>
                        <div className="flex space-x-1">
                          {(['low', 'medium', 'high'] as ImpactLevel[]).map((level) => (
                            <button
                              key={level}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleImpactChange(question.content, level);
                              }}
                              className={`px-2 py-1 text-xs font-medium rounded-full border transition-colors ${
                                currentImpact === level
                                  ? getImpactColor(level)
                                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {level.charAt(0).toUpperCase() + level.slice(1)}
                            </button>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              );
            })}

          {/* Add Custom Item */}
          {!isLoading && (
            <div className="pt-2">
              {!isAddingNew ? (
                <button
                  onClick={() => setIsAddingNew(true)}
                  className="w-full p-3 border border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-colors flex items-center justify-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span className="text-sm">Add custom item</span>
                </button>
              ) : (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-2"
                >
                  <textarea
                    value={newItemText}
                    onChange={(e) => setNewItemText(e.target.value)}
                    placeholder="Enter your custom item..."
                    className="w-full p-3 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    rows={3}
                    autoFocus
                  />
                  <div className="flex space-x-2">
                    <button
                      onClick={handleAddCustomItem}
                      disabled={!newItemText.trim() || isSavingCustom}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isSavingCustom ? 'Saving...' : 'Add Item'}
                    </button>
                    <button
                      onClick={() => {
                        setIsAddingNew(false);
                        setNewItemText('');
                      }}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </motion.div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
